<template>
  <div class="min-h-screen bg-gray-50">
    <!-- Header -->
    <header class="bg-white shadow-sm border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex justify-between items-center mb-6">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Show Management</h1>
            <p class="text-gray-600">Create and manage your trade shows</p>
          </div>
          <div class="flex space-x-3">
            <button @click="showCreateForm = true" class="btn-primary">
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"></path>
              </svg>
              Create New Show
            </button>
            <button @click="goBack" class="btn-tertiary">
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Shows Sections -->
      <div class="space-y-8">
        <!-- Happening Now -->
        <section v-if="grouped.current.length">
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Happening Now</h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="show in grouped.current" 
              :key="show.id"
              class="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-gray-200 cursor-pointer"
              @click="viewShowLeads(show)"
            >
          <!-- Show Header -->
          <div class="p-6 border-b border-gray-200">
            <div class="flex justify-between items-start">
              <div class="flex-1">
                <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                <p class="text-sm text-gray-500">
                  {{ formatDate(show.start_date) }} - {{ formatDate(show.end_date) }}
                </p>
              </div>
              <div>
                <KebabMenu>
                  <button v-if="canEditShow(show)" @click.stop="editShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Edit</button>
                  <button @click.stop="copyShowUrl(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Copy Lead Capture URL</button>
                  <button @click.stop="exportShowLeads(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Export Leads</button>
                  <button v-if="show.status !== 'declined'" @click.stop="archiveShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Archive</button>
                </KebabMenu>
              </div>
            </div>
          </div>

          <!-- Show Stats -->
          <div class="p-6">
            <div class="grid grid-cols-2 gap-4 mb-4">
              <div class="text-center">
                <div class="text-xl font-bold text-gray-900">{{ show.lead_count }}</div>
                <div class="text-gray-600">Total Leads</div>
              </div>
              <div class="text-center">
                <div class="text-xl font-bold text-gray-900">{{ show.recent_leads }}</div>
                <div class="text-gray-600">Recent (7 days)</div>
              </div>
            </div>

            <!-- Show Status -->
            <div class="flex items-center justify-between mb-4">
              <StatusBadge :status="show.status" />
              
              <!-- Recently Submitted Indicator -->
              <span
                v-if="show.status === 'quote_requested' && show.quote_requested_at"
                class="inline-flex items-center px-2 py-1 text-xs font-medium text-amber-700 bg-amber-50 rounded-full"
                title="Submitted for approval"
              >
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Submitted {{ formatRelativeTime(show.quote_requested_at) }}
              </span>
              
              <!-- Decline Reason Indicator -->
              <span
                v-if="show.status === 'declined' && show.decline_reason"
                class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full"
                title="Decline reason"
              >
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                {{ show.decline_reason }}
              </span>
              
              <!-- Recently Modified Indicator -->
              <span
                v-if="show.last_modified_at && show.status === 'quote_requested'"
                class="inline-flex items-center px-2 py-1 text-xs font-medium text-sky-700 bg-sky-50 rounded-full ml-2"
                title="Recently modified"
              >
                <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                </svg>
                Modified {{ formatRelativeTime(show.last_modified_at) }}
              </span>
              
              <span
                v-if="show.status === 'approved' && show.final_price"
                :class="[
                  'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                  show.payment_status === 'paid'
                    ? 'bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20'
                    : 'bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20'
                ]"
              >
                {{ show.payment_status === 'paid' ? 'Paid' : `€${show.final_price} - Payment Pending` }}
              </span>
              <StatusBadge 
                v-else-if="show.payment_status"
                :status="show.payment_status"
              />
            </div>

            <!-- Actions / Hover affordance -->
            <div class="mt-2 flex items-center justify-between">
              <template v-if="show.status === 'declined'">
                <button
                  @click.stop="editAndResubmit(show)"
                  class="btn-primary-sm"
                >
                  Edit & Resubmit
                </button>
                <button
                  @click.stop="archiveShow(show)"
                  class="btn-secondary-sm p-2"
                  title="Archive Show"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-14 0a2 2 0 012-2h10a2 2 0 012 2"></path>
                  </svg>
                </button>
              </template>
              <template v-else>
                <span class="text-[13px] text-gray-500 group-hover:text-gray-700">Open →</span>
                <button
                  @click.stop="exportShowLeads(show)"
                  class="btn-secondary-sm p-2"
                  title="Export Leads"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </button>
              </template>
            </div>
          </div>
        </div>
          </div>
        </section>

        <!-- Upcoming -->
        <section v-if="grouped.upcoming.length">
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Upcoming</h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="show in grouped.upcoming" 
              :key="show.id"
              class="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-gray-200 cursor-pointer"
              @click="viewShowLeads(show)"
            >
              
              <div class="p-6 border-b border-gray-200">
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                    <p class="text-sm text-gray-500">
                      {{ formatDate(show.start_date) }} - {{ formatDate(show.end_date) }}
                    </p>
                  </div>
                  <div>
                    <KebabMenu>
                      <button v-if="canEditShow(show)" @click.stop="editShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Edit</button>
                      <button @click.stop="copyShowUrl(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Copy Lead Capture URL</button>
                      <button @click.stop="exportShowLeads(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Export Leads</button>
                      <button v-if="show.status !== 'declined'" @click.stop="archiveShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Archive</button>
                    </KebabMenu>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="mb-3 flex items-center gap-2 text-[12px] text-gray-600">
                  <span>{{ show.lead_count }} total</span>
                  <span>·</span>
                  <span>{{ show.recent_leads }} recent (7d)</span>
                </div>

                <div class="flex flex-wrap items-center gap-2">
                  <StatusBadge :status="show.status" />
                  <StatusBadge v-if="show.payment_status" :status="show.payment_status === 'paid' ? 'paid' : 'payment_pending'" />
                </div>

                <div class="mt-4 flex items-center justify-between">
                  <template v-if="show.status === 'declined'">
                    <button
                      @click.stop="editAndResubmit(show)"
                      class="rounded-md bg-[#FF6B35] px-3 py-1.5 text-[13px] font-semibold text-white hover:brightness-95"
                    >
                      Edit & Resubmit
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-[13px] text-gray-500 group-hover:text-gray-700">Open →</span>
                    <button
                      @click.stop="exportShowLeads(show)"
                      class="btn-secondary-sm p-2"
                      title="Export Leads"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Past -->
        <section v-if="grouped.past.length">
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Past</h2>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div 
              v-for="show in grouped.past" 
              :key="show.id"
              class="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-gray-200 cursor-pointer"
              @click="viewShowLeads(show)"
            >
              
              <div class="p-6 border-b border-gray-200">
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                    <p class="text-sm text-gray-500">
                      {{ formatDate(show.start_date) }} - {{ formatDate(show.end_date) }}
                    </p>
                  </div>
                  <div>
                    <KebabMenu>
                      <button v-if="canEditShow(show)" @click.stop="editShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Edit</button>
                      <button @click.stop="copyShowUrl(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Copy Lead Capture URL</button>
                      <button @click.stop="exportShowLeads(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Export Leads</button>
                      <button v-if="show.status !== 'declined'" @click.stop="archiveShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Archive</button>
                    </KebabMenu>
                  </div>
                </div>
              </div>

              <div class="p-6">
                <div class="mb-3 flex items-center gap-2 text-[12px] text-gray-600">
                  <span>{{ show.lead_count }} total</span>
                  <span>·</span>
                  <span>{{ show.recent_leads }} recent (7d)</span>
                </div>
                <div class="flex flex-wrap items-center gap-2">
                  <StatusBadge :status="show.status" />
                  <StatusBadge v-if="show.payment_status" :status="show.payment_status === 'paid' ? 'paid' : 'payment_pending'" />
                </div>
                <div class="mt-4 flex items-center justify-between">
                  <template v-if="show.status === 'declined'">
                    <button
                      @click.stop="editAndResubmit(show)"
                      class="rounded-md bg-[#FF6B35] px-3 py-1.5 text-[13px] font-semibold text-white hover:brightness-95"
                    >
                      Edit & Resubmit
                    </button>
                  </template>
                  <template v-else>
                    <span class="text-[13px] text-gray-500 group-hover:text-gray-700">Open →</span>
                    <button
                      @click.stop="exportShowLeads(show)"
                      class="btn-secondary-sm p-2"
                      title="Export Leads"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                      </svg>
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Archived -->
        <section>
          <div class="mb-3 flex items-center justify-between">
            <h2 class="text-lg font-semibold text-gray-900">Archived</h2>
            <button @click="showArchived = !showArchived" class="btn-secondary-sm">{{ showArchived ? 'Hide' : 'Show' }}</button>
          </div>
          <div v-if="showArchived">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div 
                v-for="show in grouped.archived" 
                :key="show.id"
                class="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 hover:border-gray-200 cursor-pointer"
                @click="viewShowLeads(show)"
              >
                
                <div class="p-6 border-b border-gray-200">
                  <div class="flex justify-between items-start">
                    <div class="flex-1">
                      <h3 class="text-lg font-medium text-gray-900">{{ show.name }}</h3>
                      <p class="text-sm text-gray-500">
                        {{ formatDate(show.start_date) }} - {{ formatDate(show.end_date) }}
                      </p>
                    </div>
                    <div>
                      <KebabMenu>
                        <button v-if="canEditShow(show)" @click.stop="editShow(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Edit</button>
                        <button @click.stop="copyShowUrl(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Copy Lead Capture URL</button>
                        <button @click.stop="exportShowLeads(show)" class="flex w-full items-center px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50">Export Leads</button>
                      </KebabMenu>
                    </div>
                  </div>
                </div>

                <div class="p-6">
                  <div class="mb-3 flex items-center gap-2 text-[12px] text-gray-600">
                    <span>{{ show.lead_count }} total</span>
                    <span>·</span>
                    <span>{{ show.recent_leads }} recent (7d)</span>
                  </div>
                  <div class="flex flex-wrap items-center gap-2">
                    <StatusBadge status="archived" />
                  </div>
                  <div class="mt-4 flex items-center justify-between">
                    <span class="text-[13px] text-gray-500 group-hover:text-gray-700">Open →</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- Empty State -->
        <div v-if="shows.length === 0 && !loading" class="col-span-full">
          <div class="text-center py-12">
            <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"></path>
            </svg>
            <h3 class="mt-2 text-sm font-medium text-gray-900">No shows</h3>
            <p class="mt-1 text-sm text-gray-500">Get started by creating your first show.</p>
            <div class="mt-6">
              <button @click="showCreateForm = true" class="btn-primary">
                Create Show
              </button>
            </div>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="col-span-full">
          <div class="text-center py-12">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p class="mt-2 text-gray-600">Loading shows...</p>
          </div>
        </div>
      </div>
    </main>

    <!-- Create/Edit Show Modal -->
    <ShowForm
      v-if="showCreateForm || showEditForm"
      :show="editingShow"
      @close="closeForm"
      @saved="onShowSaved"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import config from '../config.js'
import { formatDate as fmtDate } from '../utils/date.js'
import ShowForm from '../components/ShowForm.vue'
import StatusBadge from '../components/StatusBadge.vue'
import KebabMenu from '../components/KebabMenu.vue'

// Router
const router = useRouter()

// Reactive data
const loading = ref(false)
const shows = ref([])
const showCreateForm = ref(false)
const showEditForm = ref(false)
const editingShow = ref(null)
const menuOpenId = ref(null)
const showArchived = ref(false)

const grouped = ref({ current: [], upcoming: [], past: [], archived: [] })

// Methods
const loadShows = async () => {
  try {
    loading.value = true
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/dashboard`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem('admin_token')
        router.push('/admin/login')
        return
      }
      throw new Error('Failed to load shows')
    }

    const data = await response.json()
    shows.value = data.shows
    
  } catch (error) {
    console.error('Error loading shows:', error)
  } finally {
    loading.value = false
  }
}

const editShow = (show) => {
  editingShow.value = show
  showEditForm.value = true
}

const closeForm = () => {
  showCreateForm.value = false
  showEditForm.value = false
  editingShow.value = null
}

const onShowSaved = () => {
  closeForm()
  loadShows()
}

const viewShowLeads = (show) => {
  router.push(`/admin/leads?show_id=${show.id}`)
}

const toggleMenu = (show) => {
  menuOpenId.value = menuOpenId.value === show.id ? null : show.id
}

const exportShowLeads = async (show) => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) return

    const response = await fetch(`${config.apiBaseUrl}/admin/leads/export?show_id=${show.id}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })

    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `leads_${show.name.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting show leads:', error)
  }
}

const copyShowUrl = async (show) => {
  try {
    const baseUrl = window.location.origin
    const url = `${baseUrl}/booth/${show.client_slug}/${show.slug}`
    
    await navigator.clipboard.writeText(url)
    // You could add a toast notification here
  } catch (error) {
    console.error('Error copying URL:', error)
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = url
    document.body.appendChild(textArea)
    textArea.select()
    document.execCommand('copy')
    document.body.removeChild(textArea)
  }
}

const goBack = () => {
  router.push('/admin/dashboard')
}

const formatDate = (dateString) => fmtDate(dateString)

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) {
    return `${diffInSeconds} seconds ago`;
  } else if (diffInSeconds < 3600) {
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
  } else if (diffInSeconds < 86400) {
    const diffInHours = Math.floor(diffInSeconds / 3600);
    return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
  } else {
    return formatDate(dateString);
  }
};

// Status helper functions - removed, now using StatusBadge component

const canEditShow = (show) => {
  return show.status === 'draft' || show.status === 'quote_requested'
}

const resubmitQuote = async (show) => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/shows/${show.id}/resubmit-quote`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        notes: show.notes || ''
      })
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to resubmit quote')
    }

    // Reload shows to reflect the status change
    await loadShows()
    
    // Show success message
    alert('Quote resubmitted successfully!')
    
  } catch (err) {
    console.error('Error resubmitting quote:', err)
    alert(`Error resubmitting quote: ${err.message}`)
  }
}

const archiveShow = async (show) => {
  try {
    const token = localStorage.getItem('admin_token')
    if (!token) {
      router.push('/admin/login')
      return
    }

    const response = await fetch(`${config.apiBaseUrl}/admin/shows/${show.id}/archive`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    })

    if (!response.ok) {
      const errorData = await response.json()
      throw new Error(errorData.error || 'Failed to archive show')
    }

    // Reload shows to reflect the status change
    await loadShows()
    
    // Show success message
    alert('Show archived successfully!')
    
  } catch (err) {
    console.error('Error archiving show:', err)
    alert(`Error archiving show: ${err.message}`)
  }
}

const editAndResubmit = async (show) => {
  editingShow.value = show
  showEditForm.value = true
  // No specific action needed here, the ShowForm component will handle the resubmission
  // when the form is submitted.
}

// Lifecycle
onMounted(() => {
  loadShows()
})

// Recompute grouped shows whenever shows change
watch(shows, () => {
  const now = new Date()
  const current = []
  const upcoming = []
  const past = []
  const archived = []

  for (const s of shows.value) {
    const start = new Date(s.start_date)
    const end = new Date(s.end_date)
    if (s.status === 'archived') {
      archived.push(s)
    } else if (start <= now && end >= now) {
      current.push(s)
    } else if (start > now) {
      upcoming.push(s)
    } else {
      past.push(s)
    }
  }

  // Sort groups
  current.sort((a, b) => new Date(a.end_date) - new Date(b.end_date))
  upcoming.sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
  past.sort((a, b) => new Date(b.end_date) - new Date(a.end_date))
  archived.sort((a, b) => new Date(b.end_date) - new Date(a.end_date))

  grouped.value = { current, upcoming, past, archived }
})
</script> 